import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Link2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { FichaEstudiante } from "@/components/FichaEstudiante";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const vacio = { nombre: "", matricula: "", grado: "", seccion: "", tutor_id: "", diagnostico: "" };
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function CoordEstudiantes() {
  const [rows, setRows] = useState(null);
  const [tutores, setTutores] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [fichaId, setFichaId] = useState(null);
  const [form, setForm] = useState(null);
  const [asig, setAsig] = useState(null);

  const cargar = () => {
    api.get("/estudiantes").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/tutores").then((r) => setTutores(r.data)).catch(() => {});
  };

  useEffect(cargar, []);

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre del estudiante");
    if (!form.matricula.trim()) return toast.error("Indique la matrícula");
    if (!form.grado.trim()) return toast.error("Indique el grado");
    const payload = { ...form };
    ["id", "creado_en", "tutor_nombre", "materia_ids"].forEach((k) => delete payload[k]);
    try {
      if (form.id) await api.put(`/estudiantes/${form.id}`, payload);
      else await api.post("/estudiantes-con-tutor", { estudiante: payload });
      toast.success(form.id ? "Ficha actualizada" : "Estudiante registrado");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const abrirAsignaciones = async (e) => {
    try {
      const { data } = await api.get(`/estudiantes/${e.id}/asignaciones`);
      setAsig(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const guardarMaterias = async () => {
    try {
      await api.put(`/estudiantes/${asig.estudiante.id}/materias`, { materia_ids: asig.materia_ids });
      toast.success("Materias del estudiante actualizadas");
      setAsig(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;

  const q = norm(busqueda.trim());
  const visibles = rows.filter((e) => !q ||
    [e.nombre, e.matricula, e.grado, e.tutor_nombre].filter(Boolean).some((v) => norm(v).includes(q)));

  return (
    <div data-testid="coord-estudiantes">
      <PageTitle title="Estudiantes"
                 subtitle="Consulte la ficha académica y asigne las materias que cursa cada estudiante.">
        <Button className="rounded-full" onClick={() => setForm({ ...vacio })} data-testid="nuevo-estudiante">
          <Plus className="mr-2 h-4 w-4" /> Nuevo estudiante
        </Button>
      </PageTitle>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="rounded-full pl-10" placeholder="Buscar por nombre, matrícula o grado…"
               value={busqueda} onChange={(e) => setBusqueda(e.target.value)} data-testid="buscar-estudiante" />
      </div>

      {visibles.length === 0 ? (
        <Empty testId="estudiantes-vacios">Ningún estudiante coincide con la búsqueda.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border/60 bg-card">
          <table className="w-full min-w-[680px] text-sm" data-testid="tabla-estudiantes">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Estudiante</th>
                <th className="px-6 py-3">Grado</th>
                <th className="px-6 py-3">Tutor</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibles.map((e) => (
                <tr key={e.id} className="border-t border-border/60">
                  <td className="px-6 py-4">
                    <button className="text-left" onClick={() => setFichaId(e.id)}
                            data-testid={`ver-ficha-${e.matricula}`}>
                      <p className="font-medium hover:text-primary">{e.nombre}</p>
                      <p className="font-mono text-xs text-muted-foreground">{e.matricula}</p>
                    </button>
                  </td>
                  <td className="px-6 py-4">{e.grado} {e.seccion}</td>
                  <td className="px-6 py-4 text-xs text-muted-foreground">{e.tutor_nombre || "—"}</td>
                  <td className="px-6 py-4">
                    <Badge variant={e.estado === "activo" ? "secondary" : "outline"}>{e.estado}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="rounded-full"
                              onClick={() => setForm(e)} data-testid={`editar-estudiante-${e.matricula}`}>
                        <Pencil className="mr-1 h-3 w-3" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full"
                              onClick={() => abrirAsignaciones(e)} data-testid={`asignaciones-${e.matricula}`}>
                        <Link2 className="mr-1 h-3 w-3" /> Materias
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FichaEstudiante estudianteId={fichaId} onClose={() => setFichaId(null)} />

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="modal-estudiante">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar ficha" : "Nuevo estudiante"}</DialogTitle>
            <DialogDescription>Datos académicos básicos del estudiante.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre completo</Label>
                <Input value={form.nombre} data-testid="form-nombre"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Matrícula</Label>
                <Input value={form.matricula} data-testid="form-matricula"
                       onChange={(e) => setForm({ ...form, matricula: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Grado</Label>
                <Input value={form.grado} data-testid="form-grado"
                       onChange={(e) => setForm({ ...form, grado: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Sección</Label>
                <Input value={form.seccion || ""}
                       onChange={(e) => setForm({ ...form, seccion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Diagnóstico (si aplica)</Label>
                <Input value={form.diagnostico || ""}
                       onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Padre / tutor responsable</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={form.tutor_id || ""} data-testid="form-tutor"
                        onChange={(e) => setForm({ ...form, tutor_id: e.target.value })}>
                  <option value="">Sin tutor</option>
                  {tutores.map((t) => <option key={t.id} value={t.id}>{`${t.nombre} — ${t.email}`}</option>)}
                </select>
              </div>
              <Button className="rounded-full py-6 sm:col-span-2" onClick={guardar}
                      data-testid="guardar-estudiante">
                Guardar ficha
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!asig} onOpenChange={(o) => !o && setAsig(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="modal-asignaciones">
          {asig && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Materias de {asig.estudiante.nombre}</DialogTitle>
                <DialogDescription>{asig.estudiante.grado} · {asig.estudiante.matricula}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {asig.materias_disponibles.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 rounded-md border border-border/60 p-3 text-sm">
                    <Checkbox checked={asig.materia_ids.includes(m.id)} data-testid={`materia-${m.id}`}
                              onCheckedChange={(v) => setAsig({
                                ...asig,
                                materia_ids: v ? [...asig.materia_ids, m.id]
                                               : asig.materia_ids.filter((x) => x !== m.id),
                              })} />
                    <span>{m.nombre}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">{m.grado}</Badge>
                  </label>
                ))}
              </div>
              <Button className="rounded-full py-6" onClick={guardarMaterias} data-testid="guardar-materias">
                Guardar materias
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
